import { CalendarDate, parseDate, today } from "@internationalized/date";

import { dateIsAfter, legalTimeZone } from "@core/utils/date";
import { CountryCode } from "@settings/countries";
import { maxPetAgeNumberOfYears, PetSex, PetSpecies } from "@settings/pet";
import { ProductId } from "@settings/products";
import { dateIsBefore, dateIsEqual, getDateXYearsAgo } from "@utils/date";

export interface Product {
  id: ProductId;
  name: string;
}

export interface Quote {
  premium: number;
  product: Product;
  selected: boolean | null;
  uuid: string;
}

export interface PetBreed {
  kb_key: string;
  name: string;
}

interface TrackingParams {
  meta_fbp: string;
  meta_fbc: string;
}

export interface ResultDTO {
  country: CountryCode;
  currency: string;
  email: string;
  payment_interval_months: number;
  pet_birth_date: string;
  pet_breed: PetBreed;
  pet_second_breed: PetBreed | null;
  pet_chip: string;
  pet_name: string;
  pet_sex: PetSex;
  pet_species: PetSpecies;
  pet_parent_address: string;
  pet_parent_birth_date: string;
  pet_parent_city: string;
  pet_parent_commercial_accepted?: boolean;
  pet_parent_country_code: string;
  pet_parent_first_name: string;
  pet_parent_salutation: string;
  pet_parent_has_friend: boolean;
  pet_parent_id_number: string;
  pet_parent_kb_key: string;
  pet_parent_last_name: string;
  pet_parent_phone: string;
  pet_parent_zip: string;
  policy_start_date: string;
  pet_purchased_products: string[];
  quotes: Quote[];
  signature_url: string;
  status: string;
  tracking_params: TrackingParams;
  uuid: string;
}
export enum UncoveredPetReason {
  PET_TOO_OLD = "PET_TOO_OLD",
  HEALTH_QUOTE_MISSING = "HEALTH_QUOTE_MISSING",
}

export class Result implements ResultDTO {
  readonly country: CountryCode;
  readonly currency: string;
  readonly email: string;
  readonly payment_interval_months: number;
  readonly pet_birth_date: string;
  readonly pet_breed: PetBreed;
  readonly pet_second_breed: PetBreed | null;
  readonly pet_chip: string;
  readonly pet_name: string;
  readonly pet_sex: PetSex;
  readonly pet_species: PetSpecies;
  readonly pet_parent_address: string;
  readonly pet_parent_birth_date: string;
  readonly pet_parent_city: string;
  readonly pet_parent_commercial_accepted?: boolean;
  readonly pet_parent_country_code: string;
  readonly pet_parent_first_name: string;
  readonly pet_parent_salutation: string;
  readonly pet_parent_has_friend: boolean;
  readonly pet_parent_id_number: string;
  readonly pet_parent_kb_key: string;
  readonly pet_parent_last_name: string;
  readonly pet_parent_phone: string;
  readonly pet_parent_zip: string;
  readonly policy_start_date: string;
  readonly pet_purchased_products: string[];
  readonly quotes: Quote[];
  readonly signature_url: string;
  readonly status: string;
  readonly tracking_params: TrackingParams;
  readonly uuid: string;

  constructor(attrs: ResultDTO) {
    Object.assign(this, attrs);
  }

  get hasHealthQuote(): boolean {
    return this.quotes.some(
      (quote: Quote) =>
        (quote.product.id === ProductId.healthBasic ||
          quote.product.id === ProductId.healthUnlimited) &&
        quote.premium > 0
    );
  }

  get hasGlQuote(): boolean {
    return this.quotes.some((quote: Quote) => quote.product.id === ProductId.glExtended);
  }

  get hasUnlimitedHealthQuote(): boolean {
    return this.quotes.some((quote: Quote) => quote.product.id === ProductId.healthUnlimited);
  }

  get hasHealthPurchased(): boolean {
    return this.pet_purchased_products.some(
      (product) => product === ProductId.healthBasic || product === ProductId.healthUnlimited
    );
  }

  get hasOnlyGlQuote(): boolean {
    return (
      this.quotes.length > 0 &&
      this.quotes.every((quote: Quote) => quote.product.id === ProductId.glExtended)
    );
  }

  get hasPreventionQuote(): boolean {
    return this.quotes.some((quote: Quote) => quote.product.id === ProductId.prevention);
  }

  get hasPolicyStartDateAfterTomorrow(): boolean {
    const legalTimeZoneDay: CalendarDate = today(legalTimeZone);
    const tomorrow = new CalendarDate(
      legalTimeZoneDay.year,
      legalTimeZoneDay.month,
      legalTimeZoneDay.day
    ).add({
      days: 1,
    });
    const parsedPolicyStartDate: CalendarDate = parseDate(this.policy_start_date);

    return dateIsAfter(parsedPolicyStartDate, tomorrow);
  }

  get uncoveredReason(): UncoveredPetReason | null {
    const maxBirthdate = getDateXYearsAgo(maxPetAgeNumberOfYears);
    const birthDate: Date = new Date(this.pet_birth_date);
    birthDate.setHours(0, 0, 0, 0);

    const petIsTooOld: boolean =
      dateIsBefore(birthDate, maxBirthdate) || dateIsEqual(birthDate, maxBirthdate);

    if (petIsTooOld) {
      return UncoveredPetReason.PET_TOO_OLD;
    }

    return null;
  }

  get isPartialPaymentAvailable(): boolean {
    return this.hasHealthQuote;
  }

  get isCrossbreedPet(): boolean {
    if (!this.pet_second_breed) {
      return false;
    }

    return true;
  }

  get isResultValidToCheckout(): boolean {
    if (this.isAlreadyPaid) return false;

    // Some users could have a browser with a different timezone than the one we use to calculate the policy start date,
    // but since we only sell in CET/CEST we are assuming nobody will try to buy outside this timezone.
    const today: string = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Berlin" });

    if (!(this.policy_start_date > today)) return false;

    if (this.hasHealthQuote) {
      const selectedHealthQuotes = this.quotes.filter(
        (quote) =>
          (quote.product.id === ProductId.healthBasic ||
            quote.product.id === ProductId.healthUnlimited) &&
          quote.selected === true
      );

      if (selectedHealthQuotes.length !== 1) return false;
    }

    if (this.hasPreventionQuote) {
      const preventionQuote = this.quotes.find(
        (quote) => quote.product.id === ProductId.prevention
      );

      if (preventionQuote?.selected !== true) {
        return false;
      }
    }

    return true;
  }

  get isAlreadyPaid(): boolean {
    return this.status === "binding" || this.status === "completed";
  }

  getQuote(productId: ProductId): Quote | undefined {
    return this.quotes.find((quote: Quote): boolean => quote.product.id === productId);
  }
}
